import React from 'react';
import { AlertTriangle, ArrowUpRight, ChevronLeft, ChevronRight, Info, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import type { SelfServicePlanState, SelfServicePlanTier } from '@/shared/apiTypes';
import { APP_Z_INDEX } from '@/ui/theme/layers';
import { defaultSelfServicePlan } from './accountPlanDefaults';

type AccountPlanPopoverProps = {
  id?: string;
  theme: ThemeClasses;
  plan: SelfServicePlanState | null | undefined;
  isActive: boolean;
  onChangePlan?: (plan: SelfServicePlanTier) => Promise<void>;
};

const planOptions: Array<{
  tier: SelfServicePlanTier;
  name: string;
  price: string;
  quota: string;
  detail: string;
  badge?: string;
}> = [
  {
    tier: 'free',
    name: 'Free',
    price: '$0',
    quota: '1 workspace / 100 tasks',
    detail: 'Core workspace views with light usage.',
  },
  {
    tier: 'pro',
    name: 'Pro',
    price: '$20/account/month',
    quota: '10 workspaces / 5k tasks',
    detail: 'Generous solo workspace limits and planned exports.',
    badge: 'Recommended',
  },
  {
    tier: 'ultra',
    name: 'Ultra',
    price: '$200/account/month',
    quota: '50 workspaces / 50k tasks',
    detail: 'Priority with planned advanced AI and automation.',
  },
];

const getPlanGuideSteps = (t: (key: string) => string): Array<{
  id: string;
  title: string;
  body: string;
  note: string;
}> => [
  {
    id: 'shadow',
    title: t('Shadow billing stays off'),
    body: t('Plan preferences are visible here, but checkout and charges stay blocked until provider readiness is proven.'),
    note: t('This guide never opens checkout or billing portal actions.'),
  },
  {
    id: 'limits',
    title: t('Limits define workspace capacity'),
    body: t('Workspaces, tasks, docs, and reports show the active quota profile for this account.'),
    note: t('Read the quota profile separately from any tier-change request.'),
  },
  {
    id: 'upgrade',
    title: t('Upgrade remains separate'),
    body: t('Pro and Ultra are shadow-mode preference changes, and Enterprise remains contact-sales only.'),
    note: t('Tier-change controls stay outside this guide.'),
  },
];

const FOCUSABLE_PLAN_GUIDE_ELEMENT_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const AccountPlanPopover: React.FC<AccountPlanPopoverProps> = ({
  id,
  theme,
  plan,
  isActive,
  onChangePlan,
}) => {
  const { t } = useSettings();
  const planGuideSteps = React.useMemo(() => getPlanGuideSteps(t), [t]);
  const generatedId = React.useId().replace(/:/g, '');
  const popoverId = id || `account-plan-popover-${generatedId}`;
  const dialogTitleId = `${popoverId}-title`;
  const dialogDescriptionId = `${popoverId}-description`;
  const planGuideId = `${popoverId}-plan-limits-guide`;
  const planGuideTitleId = `${planGuideId}-title`;
  const planGuideStepStatusId = `${planGuideId}-step-status`;
  const planGuideStepBodyId = `${planGuideId}-step-body`;
  const planGuideStepNoteId = `${planGuideId}-step-note`;
  const planGuideDescriptionIds = `${planGuideStepStatusId} ${planGuideStepBodyId} ${planGuideStepNoteId}`;
  const upgradeOptionsId = `${popoverId}-upgrade-options`;
  const activePlan = plan || defaultSelfServicePlan;
  const selectedTier = activePlan.selectedTier || activePlan.selected_tier || activePlan.tier;
  const effectiveTier = activePlan.effectiveTier || activePlan.effective_tier || selectedTier;
  const billingStatus = activePlan.billingStatus || activePlan.billing_status || activePlan.status;
  const entitlementStatus =
    activePlan.entitlementStatus || activePlan.entitlement_status || billingStatus || 'shadow';
  const displayedEffectiveTier = activePlan.entitlementsActive ? effectiveTier : 'free';
  const displayedEntitlementStatus = activePlan.entitlementsActive ? entitlementStatus : 'shadow';
  const quota = activePlan.quotaProfile || activePlan.quota_profile || defaultSelfServicePlan.quota_profile;
  const [pendingPlan, setPendingPlan] = React.useState<SelfServicePlanTier | null>(null);
  const [showUpgradeOptions, setShowUpgradeOptions] = React.useState(false);
  const [isPlanGuideOpen, setIsPlanGuideOpen] = React.useState(false);
  const [planGuideStepIndex, setPlanGuideStepIndex] = React.useState(0);
  const [planGuideStepAnnouncement, setPlanGuideStepAnnouncement] = React.useState('');
  const [error, setError] = React.useState('');
  const planGuideTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const planGuideRegionRef = React.useRef<HTMLElement | null>(null);
  const isEffectiveFree = effectiveTier === 'free' || !activePlan.entitlementsActive;
  const selectedPlanOption = planOptions.find((option) => option.tier === selectedTier);
  const effectivePlanOption = planOptions.find((option) => option.tier === displayedEffectiveTier);
  const availablePlanOptions = planOptions.filter((option) => option.tier !== selectedTier);
  const currentPlanGuideStep = planGuideSteps[planGuideStepIndex];
  const previousPlanGuideStep = planGuideStepIndex > 0 ? planGuideSteps[planGuideStepIndex - 1] : null;
  const nextPlanGuideStep = planGuideStepIndex < planGuideSteps.length - 1 ? planGuideSteps[planGuideStepIndex + 1] : null;
  const planGuideStepStatusLabel = t('Step {{current}} of {{total}}', {
    current: planGuideStepIndex + 1,
    total: planGuideSteps.length,
  });
  const previousPlanGuideLabel = previousPlanGuideStep
    ? t('Back to step {{step}}: {{title}}', {
      step: planGuideStepIndex,
      title: previousPlanGuideStep.title,
    })
    : t('Back');
  const nextPlanGuideLabel = nextPlanGuideStep
    ? t('Next to step {{step}}: {{title}}', {
      step: planGuideStepIndex + 2,
      title: t(nextPlanGuideStep.title),
    })
    : t('Finish and close plan guide');
  const isFirstPlanGuideStep = planGuideStepIndex === 0;
  const isLastPlanGuideStep = planGuideStepIndex === planGuideSteps.length - 1;

  const buildPlanGuideStepAnnouncement = (stepIndex: number) => {
    const step = planGuideSteps[stepIndex];
    const status = t('Step {{current}} of {{total}}', {
      current: stepIndex + 1,
      total: planGuideSteps.length,
    });
    return `${status}: ${step.title}`;
  };

  React.useEffect(() => {
    if (!isActive) {
      setIsPlanGuideOpen(false);
      setPlanGuideStepIndex(0);
      setPlanGuideStepAnnouncement('');
      setShowUpgradeOptions(false);
      setError('');
    }
  }, [isActive]);

  React.useEffect(() => {
    if (!isPlanGuideOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      planGuideRegionRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isPlanGuideOpen]);

  const getPlanGuideFocusableElements = React.useCallback(() => {
    const planGuide = planGuideRegionRef.current;
    if (!planGuide) {
      return [];
    }

    return Array.from(planGuide.querySelectorAll<HTMLElement>(FOCUSABLE_PLAN_GUIDE_ELEMENT_SELECTOR))
      .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
  }, []);

  React.useEffect(() => {
    if (!isPlanGuideOpen) {
      return undefined;
    }

    const handlePlanGuideTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const planGuide = planGuideRegionRef.current;
      if (!planGuide) {
        return;
      }

      const focusableElements = getPlanGuideFocusableElements();
      const firstElement = focusableElements[0] || planGuide;
      const lastElement = focusableElements[focusableElements.length - 1] || planGuide;
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (!activeElement || !planGuide.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
        return;
      }

      if (focusableElements.length === 0) {
        event.preventDefault();
        planGuide.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handlePlanGuideTab);
    return () => document.removeEventListener('keydown', handlePlanGuideTab);
  }, [getPlanGuideFocusableElements, isPlanGuideOpen]);

  const restorePlanGuideTriggerFocus = () => {
    window.setTimeout(() => {
      planGuideTriggerRef.current?.focus();
    }, 0);
  };

  const handlePlanChange = async (nextPlan: SelfServicePlanTier) => {
    if (!onChangePlan || nextPlan === selectedTier) {
      return;
    }
    setError('');
    setPendingPlan(nextPlan);
    try {
      await onChangePlan(nextPlan);
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : t('Failed to update plan.'));
    } finally {
      setPendingPlan(null);
    }
  };

  const openPlanGuide = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setPlanGuideStepIndex(0);
    setPlanGuideStepAnnouncement('');
    setShowUpgradeOptions(false);
    setError('');
    setIsPlanGuideOpen(true);
  };

  const closePlanGuide = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsPlanGuideOpen(false);
    setPlanGuideStepIndex(0);
    setPlanGuideStepAnnouncement('');
    restorePlanGuideTriggerFocus();
  };

  const showPreviousPlanGuideStep = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const nextIndex = Math.max(0, planGuideStepIndex - 1);
    setPlanGuideStepIndex(nextIndex);
    setPlanGuideStepAnnouncement(buildPlanGuideStepAnnouncement(nextIndex));
  };

  const showNextPlanGuideStep = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isLastPlanGuideStep) {
      setIsPlanGuideOpen(false);
      setPlanGuideStepIndex(0);
      setPlanGuideStepAnnouncement('');
      restorePlanGuideTriggerFocus();
      return;
    }
    const nextIndex = Math.min(planGuideSteps.length - 1, planGuideStepIndex + 1);
    setPlanGuideStepIndex(nextIndex);
    setPlanGuideStepAnnouncement(buildPlanGuideStepAnnouncement(nextIndex));
  };

  const toggleUpgradeOptions = () => {
    setIsPlanGuideOpen(false);
    setPlanGuideStepIndex(0);
    setPlanGuideStepAnnouncement('');
    setShowUpgradeOptions((current) => !current);
  };

  return (
    <div
      id={popoverId}
      data-testid="account-plan-popover"
      role="dialog"
      aria-labelledby={dialogTitleId}
      aria-describedby={dialogDescriptionId}
      className={cn(
        'absolute left-4 right-4 top-full mt-2 w-auto rounded-lg border p-4 shadow-lg transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none sm:left-auto sm:right-0 sm:w-[min(24rem,calc(100vw-2rem))]',
        isActive ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-1 scale-[0.985] opacity-0',
        theme.bgPrimary,
        theme.borderPrimary,
      )}
      style={{ zIndex: APP_Z_INDEX.headerDropdown }}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', theme.accentBlue)}>
          <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p id={dialogTitleId} className={cn('text-sm font-semibold', theme.textPrimary)}>{t('Account')}</p>
          <p id={dialogDescriptionId} className={cn('mt-1 text-xs leading-5', theme.textSecondary)}>
            {t('Your current plan and upgrade path. CommerceOS billing is shadow-only; no checkout or charge is active.')}
          </p>
        </div>
      </div>

      <button
        type="button"
        ref={planGuideTriggerRef}
        data-testid="plan-limits-guide-trigger"
        aria-expanded={isPlanGuideOpen}
        aria-controls={planGuideId}
        onClick={openPlanGuide}
        className={cn(
          'mt-4 inline-flex h-9 min-h-[36px] w-full items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          'hover:border-blue-400',
        )}
      >
        <Info className="h-4 w-4" aria-hidden="true" />
        {t('Plan & limits guide')}
      </button>

      {isPlanGuideOpen ? (
        <section
          ref={planGuideRegionRef}
          id={planGuideId}
          role="region"
          data-testid="plan-limits-guide"
          aria-labelledby={planGuideTitleId}
          aria-describedby={planGuideDescriptionIds}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          className={cn('mt-3 rounded-md border p-3 focus:outline-none', theme.bgSecondary, theme.borderPrimary)}
        >
          <div
            data-testid="plan-limits-guide-live-status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            {planGuideStepAnnouncement}
          </div>
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <p
                id={planGuideStepStatusId}
                data-testid="plan-limits-guide-step-status"
                aria-current="step"
                className={cn('text-xs font-semibold uppercase', theme.textMuted)}
              >
                {planGuideStepStatusLabel}
              </p>
              <h3 id={planGuideTitleId} data-testid="plan-limits-guide-title" className={cn('mt-1 text-sm font-semibold', theme.textPrimary)}>
                {currentPlanGuideStep.title}
              </h3>
              <p id={planGuideStepBodyId} className={cn('mt-2 text-xs leading-5', theme.textSecondary)}>
                {currentPlanGuideStep.body}
              </p>
              <p id={planGuideStepNoteId} className={cn('mt-2 text-xs font-medium leading-5', theme.infoState)}>
                {currentPlanGuideStep.note}
              </p>
            </div>
            <button
              type="button"
              data-testid="plan-limits-guide-close"
              aria-label={t('Close plan guide')}
              onClick={closePlanGuide}
              className={cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors', theme.textMuted, 'hover:bg-black/5 dark:hover:bg-white/10')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              data-testid="plan-limits-guide-back"
              disabled={isFirstPlanGuideStep}
              aria-label={previousPlanGuideLabel}
              onClick={showPreviousPlanGuideStep}
              className={cn(
                'inline-flex h-8 min-w-[5.5rem] items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors disabled:cursor-default disabled:opacity-50',
                theme.bgPrimary,
                theme.borderPrimary,
                theme.textSecondary,
              )}
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              {t('Back')}
            </button>
            <button
              type="button"
              data-testid="plan-limits-guide-next"
              aria-label={nextPlanGuideLabel}
              onClick={showNextPlanGuideStep}
              className={cn(
                'inline-flex h-8 min-w-[5.5rem] items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
                theme.primaryAction,
              )}
            >
              {isLastPlanGuideStep ? t('Finish') : t('Next')}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </section>
      ) : null}

      <div className={cn('mt-4 rounded-md border p-3', theme.bgSecondary, theme.borderPrimary)}>
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div>
            <p className={cn('text-xs font-semibold uppercase', theme.textMuted)}>{t('Plan preference')}</p>
            <p
              data-testid="account-plan-selected"
              data-plan-id={selectedTier}
              data-plan-price={selectedPlanOption?.price || activePlan.priceLabel || activePlan.price_label}
              className={cn('mt-1 text-lg font-semibold', theme.textPrimary)}
            >
              {t(selectedPlanOption?.name || activePlan.name)}{' '}
              <span className={cn('text-sm font-medium', theme.infoState)}>{t(activePlan.priceLabel || activePlan.price_label)}</span>
            </p>
          </div>
          <span
            data-testid="account-plan-shadow-badge"
            data-shadow-mode="true"
            className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold', theme.accentYellow)}
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {t('Shadow')}
          </span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="col-span-2">
            <dt className={theme.textMuted}>{t('Effective access')}</dt>
            <dd
              data-testid="account-plan-effective-access"
              data-plan-id={displayedEffectiveTier}
              data-entitlement-status={displayedEntitlementStatus}
              className={theme.textPrimary}
            >
              {t(effectivePlanOption?.name || 'Free')} · {t(displayedEntitlementStatus)}
            </dd>
          </div>
          <div>
            <dt className={theme.textMuted}>{t('Workspaces')}</dt>
            <dd className={theme.textPrimary}>{quota.workspaces}</dd>
          </div>
          <div>
            <dt className={theme.textMuted}>{t('Tasks')}</dt>
            <dd className={theme.textPrimary}>{quota.tasks.toLocaleString()}</dd>
          </div>
          <div>
            <dt className={theme.textMuted}>{t('Docs')}</dt>
            <dd className={theme.textPrimary}>{quota.docs.toLocaleString()}</dd>
          </div>
          <div>
            <dt className={theme.textMuted}>{t('Reports')}</dt>
            <dd className={theme.textPrimary}>{quota.reports.toLocaleString()}</dd>
          </div>
        </dl>
        <button
          type="button"
          aria-expanded={showUpgradeOptions}
          aria-controls={showUpgradeOptions ? upgradeOptionsId : undefined}
          onClick={toggleUpgradeOptions}
          className={cn(
            'mt-3 inline-flex h-9 min-h-[36px] w-full items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors',
            theme.primaryAction,
          )}
        >
          {isEffectiveFree ? t('Upgrade') : t('Change preference')}
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <p className={cn('mt-2 text-xs leading-5', theme.textSecondary)}>
          {t('Preference is not paid access. Effective access stays Free until provider billing and entitlements are activated.')}
        </p>
      </div>

      {showUpgradeOptions ? (
        <div
          id={upgradeOptionsId}
          data-testid="account-plan-options"
          className="mt-3 grid gap-2"
          aria-label={t('Upgrade options')}
        >
          {availablePlanOptions.map((option) => {
          const isPending = pendingPlan === option.tier;
          return (
            <button
              key={option.tier}
              type="button"
              data-plan-id={option.tier}
              data-plan-price={option.price}
              disabled={Boolean(pendingPlan)}
              onClick={() => void handlePlanChange(option.tier)}
              className={cn(
                'min-w-0 rounded-md border p-3 text-left text-sm transition-colors',
                cn(theme.bgSecondary, theme.borderPrimary, theme.textPrimary, 'hover:border-blue-400'),
                Boolean(pendingPlan) && 'disabled:cursor-default disabled:opacity-80',
              )}
            >
              <span className="flex min-w-0 items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-semibold">{t(option.name)}</span>
                    {option.badge ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                        {t(option.badge)}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 block text-xs">{t(option.price)} · {t(option.quota)}</span>
                  <span className={cn('mt-1 block text-xs leading-5', theme.textSecondary)}>
                    {t(option.detail)}
                  </span>
                </span>
                {isPending ? <span className={cn('shrink-0 text-xs', theme.textSecondary)}>{t('Saving')}</span> : null}
              </span>
            </button>
          );
        })}
          <div className={cn('rounded-md border px-3 py-2 text-sm', theme.bgSecondary, theme.borderPrimary)}>
            <span className={cn('font-semibold', theme.textPrimary)}>{t('Enterprise')}</span>
            <span className={cn('ml-2', theme.textSecondary)}>{t('Domain users and seat billing are contact-sales only.')}</span>{' '}
            <a className={cn('font-medium underline', theme.infoState)} href="mailto:sales@dope-ai.kr?subject=Management%20Tool%20Enterprise">
              {t('Contact sales')}
            </a>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="mt-3 text-sm text-red-500" role="alert">{error}</p>
      ) : null}
    </div>
  );
};
